import { useEffect, useState } from "react";
import { useAuth } from "../auth";
import { navigate, usePath } from "../router";
import { api, rememberHostToken } from "../api";
import { Ico } from "./shell";

/** Fixed top banner offering to jump back into the host's live session. Shown
   on any page (cross-device, server-tracked) except the host screen itself. */
export default function ResumeBanner() {
  const { user } = useAuth();
  const path = usePath();
  const [active, setActive] = useState<{ pin: string; quizTitle: string; hostToken: string } | null>(null);
  // Remember a dismissed session (by PIN) so the 30s re-poll doesn't resurrect
  // it; a *different* live session still shows the banner again.
  const [dismissedPin, setDismissedPin] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem("bk_resume_dismissed");
    } catch {
      return null;
    }
  });

  // Hide on the host screen itself and on the phone remote controller — both are
  // already "inside" a live session, so a resume prompt would be redundant.
  const onHostPage = path.startsWith("/host/") || path.startsWith("/remote/");

  useEffect(() => {
    if (!user || onHostPage) {
      setActive(null);
      return;
    }
    let cancelled = false;
    const check = async () => {
      try {
        const r = await api.activeSession();
        if (!cancelled) setActive(r.active);
      } catch {
        if (!cancelled) setActive(null);
      }
    };
    check();
    const id = setInterval(check, 30000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [user, path, onHostPage]);

  if (!active || onHostPage || active.pin === dismissedPin) return null;

  const resume = () => {
    rememberHostToken(active.pin, active.hostToken);
    navigate(`/host/${active.pin}`);
  };
  const dismiss = () => {
    setDismissedPin(active.pin);
    try {
      sessionStorage.setItem("bk_resume_dismissed", active.pin);
    } catch {
      /* sessionStorage unavailable — dismissal lasts until reload */
    }
  };
  const formattedPin = active.pin.length === 6 ? `${active.pin.slice(0, 3)} ${active.pin.slice(3)}` : active.pin;

  return (
    <div
      style={{
        position: "fixed",
        left: "50%",
        top: 14,
        transform: "translateX(-50%)",
        zIndex: 90,
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "9px 12px 9px 18px",
        background: "#231A12",
        color: "#fff",
        borderRadius: 999,
        boxShadow: "0 14px 34px rgba(40,25,15,.28)",
        fontWeight: 600,
        fontSize: 13.5,
        maxWidth: "94vw",
      }}
    >
      <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        Live session running ·{" "}
        <b style={{ fontFamily: '"Sprig", Georgia, serif', fontStyle: "italic", fontWeight: 600 }}>{active.quizTitle}</b>{" "}
        <span style={{ fontFamily: '"Space Mono", monospace', opacity: 0.8 }}>PIN {formattedPin}</span>
      </span>
      <button
        onClick={resume}
        style={{
          flex: "0 0 auto",
          border: "none",
          cursor: "pointer",
          background: "#AE8536",
          color: "#3a2a08",
          borderRadius: 999,
          padding: "7px 15px",
          fontFamily: "inherit",
          fontWeight: 700,
          fontSize: 12.5,
          whiteSpace: "nowrap",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <Ico name="play" size={13} /> Resume
      </button>
      <button
        onClick={dismiss}
        title="Hide"
        aria-label="Hide live session banner"
        style={{
          flex: "0 0 auto",
          border: "none",
          cursor: "pointer",
          background: "rgba(255,255,255,.14)",
          color: "#fff",
          borderRadius: "50%",
          width: 26,
          height: 26,
          fontSize: 14,
          fontWeight: 700,
          lineHeight: 1,
          display: "grid",
          placeItems: "center",
        }}
      >
        ✕
      </button>
    </div>
  );
}
